import { NextPageContext } from 'next'
import { Config } from '@chegg-wt/core/config'
import { createCheggInstance } from '@chegg/optimizely-sdk'
import { ReactSDKClient } from '@optimizely/react-sdk'
import { getOrSetVCookieServer } from '@chegg-wt/core/utils/getVCookie'
import {
  FORCE_EXP_KEY,
  GUEST_CITATION_VALIDITY_CM,
  GUEST_CITATION_VALIDITY_EB,
  GuestCitationValidityTestCells,
  LOW_AD_CM,
  LOW_AD_CONTROL_CELL,
  LOW_AD_DEFAULT_CM,
  LOW_AD_DEFAULT_EB,
  LOW_AD_EB,
  LOW_AD_VARIATIONS,
  PRICING_EXP_VARIATIONS,
  Q423_PRICING_TEST_FEATURE_CM,
  Q423_PRICING_TEST_FEATURE_EB,
} from '@chegg-wt/core/state/optimizely/constants'
import { BackendOpts } from '@chegg-wt/core/state/featureFlags/types'
// import { getSearchWidgetExpAdConfigVersion } from '../state/optimizely/utils'
import { trackWithoutThrow } from './errors'
import {
  getBackendOpts,
  //  Cookies
} from './cookies'

let optimizely: ReactSDKClient

export const getBackendOptsSafe = (ctx: NextPageContext) => {
  try {
    return getBackendOpts(ctx)
  } catch (e) {
    trackWithoutThrow(e)
    return {}
  }
}

type NextPageContextWithBackendsOpts = NextPageContext & {
  req: { backendOpts: BackendOpts; cookies: { [key: string]: string } }
}

export const getBackendOptsWithOptimizely = async (
  ctx: NextPageContextWithBackendsOpts
) => {
  if (ctx?.req?.backendOpts) {
    return Promise.resolve(ctx.req.backendOpts)
  }

  const backendOpts: BackendOpts = {}
  const { tokenSiteCode } = Config
  // const userRole = ctx.req.cookies[Cookies.UserRole]

  if (!optimizely) {
    optimizely = createCheggInstance({
      sdkKey: Config.optimizelySdkKey,
      cookieDomain: Config.cookieDomain,
    }).optimizely
    await optimizely.onReady()
  }
  const vCookie = getOrSetVCookieServer(Config.cookieDomain, ctx)

  // setting force experiments for low ad test in "if" statement.
  const forceLowAdEbExp = `${FORCE_EXP_KEY}low-ad-eb-exp`
  if (ctx.query[forceLowAdEbExp]) {
    const variation = ctx.query[forceLowAdEbExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (LOW_AD_VARIATIONS[variation] || LOW_AD_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(LOW_AD_EB, vCookie, { siteName: tokenSiteCode })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      LOW_AD_EB,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        siteName: tokenSiteCode,
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (adConfigVersion !== LOW_AD_DEFAULT_EB) {
      backendOpts.adConfigVersion = adConfigVersion
    }
  }

  // setting force experiments for low ad test in "if" statement.
  const forceLowAdCmExp = `${FORCE_EXP_KEY}low-ad-cm-exp`
  if (ctx.query[forceLowAdCmExp]) {
    const variation = ctx.query[forceLowAdCmExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (LOW_AD_VARIATIONS[variation] || LOW_AD_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(LOW_AD_CM, vCookie, { siteName: tokenSiteCode })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      LOW_AD_CM,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        siteName: tokenSiteCode,
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (adConfigVersion !== LOW_AD_DEFAULT_CM) {
      backendOpts.adConfigVersion = adConfigVersion
    }
  }

  // setting force experiments for Q4 23 Pricing Test on EB in "if" statement.
  const forceQ423PricingTestEB = `${FORCE_EXP_KEY}q423-pricing-exp-eb`
  if (ctx.query[forceQ423PricingTestEB]) {
    const variation = ctx.query[forceQ423PricingTestEB]
    backendOpts.pricingExpKey =
      typeof variation === 'string' &&
      (PRICING_EXP_VARIATIONS[variation] || PRICING_EXP_VARIATIONS.control)
  } else if (
    optimizely.isFeatureEnabled(Q423_PRICING_TEST_FEATURE_EB, vCookie, {
      siteName: tokenSiteCode,
    })
  ) {
    const pricingExpKey = optimizely.getFeatureVariableString(
      Q423_PRICING_TEST_FEATURE_EB,
      'pricingExpKey',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        wtSiteName: tokenSiteCode,
        wtCountryCode: 'US',
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (pricingExpKey !== PRICING_EXP_VARIATIONS.control) {
      backendOpts.pricingExpKey = pricingExpKey
    }
  }

  // setting force experiments for Q4 23 Pricing Test on CM in "if" statement.
  const forceQ423PricingTestCM = `${FORCE_EXP_KEY}q423-pricing-exp-cm`
  if (ctx.query[forceQ423PricingTestCM]) {
    const variation = ctx.query[forceQ423PricingTestCM]
    backendOpts.pricingExpKey =
      typeof variation === 'string' &&
      (PRICING_EXP_VARIATIONS[variation] || PRICING_EXP_VARIATIONS.control)
  } else if (
    optimizely.isFeatureEnabled(Q423_PRICING_TEST_FEATURE_CM, vCookie, {
      siteName: tokenSiteCode,
    })
  ) {
    const pricingExpKey = optimizely.getFeatureVariableString(
      Q423_PRICING_TEST_FEATURE_CM,
      'pricingExpKey',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        wtSiteName: tokenSiteCode,
        wtCountryCode: 'US',
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (pricingExpKey !== PRICING_EXP_VARIATIONS.control) {
      backendOpts.pricingExpKey = pricingExpKey
    }
  }

  // setting force experiments for Search Widget Exp Test CM in "if" statement.

  // setting force experiments for Guest Citation Validity Exp Test CM in "if" statement.
  const forceGuestCitationValidityCM = `${FORCE_EXP_KEY}${GUEST_CITATION_VALIDITY_CM}`
  if (ctx.query[forceGuestCitationValidityCM]) {
    const variation = ctx.query[forceGuestCitationValidityCM]
    backendOpts.adConfigVersion =
      (typeof variation === 'string' && GuestCitationValidityTestCells[variation]) ||
      GuestCitationValidityTestCells.control
  } else if (
    optimizely.isFeatureEnabled(GUEST_CITATION_VALIDITY_CM, vCookie, {
      siteName: tokenSiteCode,
    })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      GUEST_CITATION_VALIDITY_CM,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        wtSiteName: tokenSiteCode,
      }
    )
    backendOpts.adConfigVersion = adConfigVersion
  }

  // setting force experiments for Guest Citation Validity Exp Test EB in "if" statement.
  const forceGuestCitationValidityEB = `${FORCE_EXP_KEY}${GUEST_CITATION_VALIDITY_EB}`
  if (ctx.query[forceGuestCitationValidityEB]) {
    const variation = ctx.query[forceGuestCitationValidityEB]
    backendOpts.adConfigVersion =
      (typeof variation === 'string' && GuestCitationValidityTestCells[variation]) ||
      GuestCitationValidityTestCells.control
  } else if (
    optimizely.isFeatureEnabled(GUEST_CITATION_VALIDITY_EB, vCookie, {
      siteName: tokenSiteCode,
    })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      GUEST_CITATION_VALIDITY_EB,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        wtSiteName: tokenSiteCode,
      }
    )
    backendOpts.adConfigVersion = adConfigVersion
  }

  const mergedBackendOpts = Object.assign(getBackendOpts(ctx), backendOpts) as BackendOpts
  if (ctx?.req) {
    ctx.req.backendOpts = mergedBackendOpts
  }
  return mergedBackendOpts
}

export const getBackendOptsWithOptimizelySafe = async (
  ctx: NextPageContextWithBackendsOpts
): Promise<BackendOpts> => {
  try {
    return getBackendOptsWithOptimizely(ctx)
  } catch (e) {
    trackWithoutThrow(e)
    return {}
  }
}
